import { useParams, Link, Navigate } from "react-router-dom";
import SeoLayout from "@/components/SeoLayout";
import Jsonld, { SeoMeta } from "@/components/Jsonld";
import BookingWizard from "@/components/BookingWizard";
import RepairVsReplace from "@/components/RepairVsReplace";
import Testimonials from "@/components/Testimonials";
import { APPLIANCES, BRANDS, CLIMATE_CALENDAR, faqFor } from "@/data/seo";
import { reviewsForAppliance, REVIEWS } from "@/data/reviews";
import { Check, ListChecks, Thermometer, Sparkles, Wrench } from "lucide-react";

export default function ServiceDetail() {
  const { appliance: slug } = useParams();
  const app = APPLIANCES.find((a) => a.slug === slug);
  if (!app) return <Navigate to="/" replace />;

  const monthKey = String(new Date().getMonth() + 1).padStart(2, "0");
  const climate = CLIMATE_CALENDAR[monthKey];

  const priceMid = Math.round(app.replacement_cost * 0.2);
  const priceLow = Math.round(app.replacement_cost * 0.1);
  const priceHigh = Math.round(app.replacement_cost * 0.35);

  const title = `${app.display} in San Antonio · Same-Day · $${priceLow}-$${priceHigh} | Space Appliance`;
  const description = `Same-day ${app.name.toLowerCase()} repair in San Antonio. Factory-trained on Samsung, LG, Whirlpool. Flat-rate diagnostic. Written quotes. Call (210) 800-1710.`;

  const faqs = faqFor(app);
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service",
    serviceType: app.display, name: `${app.display} in San Antonio`,
    provider: { "@type": "LocalBusiness", name: "Space Appliance Repair", telephone: "+1-210-800-1710",
      address: { "@type": "PostalAddress", streetAddress: "5504 Bandera Rd", addressLocality: "San Antonio", addressRegion: "TX", postalCode: "78238" } },
    areaServed: { "@type": "City", name: "San Antonio" },
    offers: { "@type": "AggregateOffer", lowPrice: priceLow, highPrice: priceHigh, priceCurrency: "USD" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "111" },
  };

  return (
    <SeoLayout>
      <SeoMeta title={title} description={description} />
      <Jsonld id={`ld-svc-${slug}`} data={serviceLd} />
      <Jsonld id={`ld-faq-${slug}`} data={faqLd} />

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 pb-12">
        <div className="chip"><Wrench className="w-3.5 h-3.5" /> San Antonio · Same-day</div>
        <h1 className="display-retro mt-5 text-cream text-4xl sm:text-6xl normal-case font-semibold leading-[1.05]">
          {app.display} in <span className="text-orange">San Antonio</span><span className="text-gold">.</span>
        </h1>
        <p className="mt-4 text-lg text-cream/80 max-w-2xl leading-relaxed">
          Same-day in-home {app.name.toLowerCase()} repair across Bexar County. Most repairs run <span className="text-orange font-semibold">${priceLow}–${priceHigh}</span> with
          a flat-rate diagnostic credited toward the work. We carry the most-failed {app.name.toLowerCase()} parts on every truck.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#book" data-testid={`svc-${slug}-book-btn`}><button className="btn-orange label-retro text-sm">Book appointment</button></a>
          <a href="tel:+12108001710"><button className="btn-ghost label-retro text-sm">(210) 800-1710</button></a>
        </div>
      </section>

      {/* COMMON ISSUES + DIAGNOSTICS */}
      <section className="bg-navy-2/60 border-y border-cream/10 py-14">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Common symptoms</div>
            <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-5">What we see most</h2>
            <ul className="space-y-2.5">
              {app.common_issues.map((i) => (
                <li key={i} className="flex items-start gap-3 text-cream/85"><ListChecks className="w-4 h-4 text-orange mt-1 shrink-0" /> {i}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Our diagnostic playbook</div>
            <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-5">How we diagnose</h2>
            <ol className="space-y-2.5">
              {app.diagnostics.map((d, i) => (
                <li key={d} className="flex items-start gap-3 text-cream/85">
                  <span className="display-retro text-orange w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span> {d}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CLIMATE PANEL */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
        <div className="bg-orange/8 border border-orange/40 rounded-xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <Thermometer className="w-6 h-6 text-orange mt-1 shrink-0" />
            <div>
              <div className="text-xs uppercase tracking-widest text-orange label-retro">San Antonio · this month</div>
              <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mt-1">{climate.season}</h2>
              <p className="text-cream/80 mt-3">{climate.note}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {climate.top_failures.map((f) => (
                  <span key={f} className="text-xs label-retro uppercase tracking-widest border border-orange/40 text-orange rounded-full px-3 py-1.5">{f}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTS WE STOCK */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-14">
        <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">In-truck inventory</div>
        <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-5">Parts on the van today</h2>
        <p className="text-cream/70 mb-5 max-w-2xl">~92% of {app.name.toLowerCase()} jobs finish on the first visit because we don't drive back to the shop for these:</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {app.parts_typical.map((p) => (
            <div key={p} className="bg-navy-2/60 border border-cream/15 rounded-md p-4 flex items-center gap-3">
              <Check className="w-4 h-4 text-orange shrink-0" />
              <span className="text-cream/85 text-sm">{p}</span>
            </div>
          ))}
        </div>
      </section>

      {/* REPAIR vs REPLACE */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <RepairVsReplace applianceSlug={slug} />
      </section>

      {/* TESTIMONIALS (appliance-filtered) */}
      <Testimonials
        reviews={(() => {
          const filtered = reviewsForAppliance(app.name, 6);
          return filtered.length >= 3 ? filtered : REVIEWS.slice(0, 6);
        })()}
        itemName={app.display}
        id={`ld-reviews-${slug}`}
      />

      {/* BOOKING */}
      <section id="book" className="py-14 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="text-center">
            <div className="chip"><Sparkles className="w-3.5 h-3.5" /> Mission Control · Booking</div>
            <h2 className="display-retro mt-4 text-cream text-3xl sm:text-4xl uppercase">Book appointment</h2>
            <p className="mt-3 text-cream/70 max-w-xl mx-auto">
              60 seconds. Code <span className="text-gold font-semibold tracking-widest">GRAND_OPENING</span> saves you $10.
            </p>
          </div>
          <div className="mt-10"><BookingWizard variant="embed" /></div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 border-t border-cream/10">
        <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Frequently asked</div>
        <h2 className="display-retro text-cream text-2xl sm:text-3xl uppercase mb-6">About {app.name.toLowerCase()} repair in San Antonio</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="group bg-navy-2/60 border border-cream/15 rounded-md p-5" data-testid={`faq-${slug}-${i}`}>
              <summary className="display text-cream cursor-pointer list-none flex items-center justify-between gap-4">
                <span className="text-base sm:text-lg">{f.q}</span>
                <span className="text-orange group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-cream/75 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10 border-t border-cream/10 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Other services</div>
          <div className="flex flex-wrap gap-2">
            {APPLIANCES.filter((a) => a.slug !== slug).map((a) => (
              <Link key={a.slug} to={`/services/${a.slug}`} data-testid={`xlink-svc-${a.slug}`} className="text-sm text-cream/80 hover:text-orange border border-cream/15 rounded-full px-3 py-1.5 transition-colors">
                {a.name}
              </Link>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-orange label-retro mb-3">Brands we service</div>
          <div className="flex flex-wrap gap-2">
            {BRANDS.slice(0, 10).map((b) => (
              <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`xlink-brand-${b.slug}`} className="text-sm text-cream/80 hover:text-orange border border-cream/15 rounded-full px-3 py-1.5 transition-colors">
                {b.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SeoLayout>
  );
}
